/**
 * Created by Shen Mo on 2019/10/22
 */
import { assign, isPlainObject, isFunction, pickBy, reduce } from 'lodash-es';

const compose = (...fns) => {
	if (fns.length === 0) {
		return (args) => args;
	}
	if (fns.length === 1) {
		return fns[0];
	}

	return reduce(fns, (fnA, fnB) => {
		return (...args) => fnA(fnB(...args));
	});
};

const enhancer = (createStore, middlewareList) => (processor, init) => {
	const store = createStore(processor, init);
	let dispatch = () => {
		throw new Error('FAIL ENHANCE store & processor');
		// 在进行 middleware 包装失败时，会触发到错误信息
	};
	const _enhanceAPI = {
		getState: store.getState,
		dispatch: (action, ...args) => dispatch(action, ...args),
	};
	middlewareList = middlewareList.map((m) => m(_enhanceAPI));

	dispatch = compose(...middlewareList)(store.dispatch);

	return {
		...store,
		dispatch,
	};
};

function createStore(processor, init, middleware = null) {
	const currentProcessor = processor;
	let currentState = init;
	let middlewareList = middleware ? [].concat(middleware) : null;
	let currentListeners = [];
	let nextListeners = currentListeners;
	let isDispatching = false;

	if (middlewareList) {
		return enhancer(createStore, middlewareList)(processor, init);
	}

	const _ensureCanMutateNextListeners = () => {
		if (nextListeners === currentListeners) {
			nextListeners = currentListeners.slice();
		}
	};

	const addChangeListener = (listener) => {
		if (!isFunction(listener)) {
			throw new Error('INVALID listener', listener);
		}
		let isSubscribed = true;
		_ensureCanMutateNextListeners();
		nextListeners.push(listener);

		return function removeChangeListener() {
			if (!isSubscribed) {
				return;
			}

			isSubscribed = false;
			_ensureCanMutateNextListeners();
			const index = nextListeners.indexOf(listener);
			nextListeners.splice(index, 1);
		};
	};

	const getState = () => {
		return currentState;
	};
	const dispatch = (action) => {
		if (!isPlainObject(action)) {
			throw new Error('[ActionErr]');
		}
		if (typeof action.type === 'undefined') {
			throw new Error('[ActionTypeErr]');
		}
		if (isDispatching) {
			throw new Error('[DispatchingErr]');
		}

		try {
			isDispatching = true;
			currentState = currentProcessor(currentState, action);
		} finally {
			isDispatching = false;
		}
		let listeners = (currentListeners = nextListeners);
		for (let i = 0; i < listeners.length; i++) {
			listeners[i](action);
		}

		return action;
	};

	dispatch({ type: '_INIT_' });

	return {
		dispatch,
		addChangeListener,
		getState,
	};
}

function combineProcessor(processors) {
	const finalProcessors = pickBy(processors, isFunction);
	const finalPKeys = Object.keys(finalProcessors);

	return function combination(state = {}, action) {
		let hasChanged = false;
		let nextState = {};
		for (let i = 0; i < finalPKeys.length; i++) {
			const key = finalPKeys[i];
			const reducer = finalProcessors[key];
			const previousStateForKey = state[key];
			const nextStateForKey = reducer(previousStateForKey, action);
			// 如果reducer返回undefined则抛出错误
			if (typeof nextStateForKey === 'undefined') {
				throw new Error('[PErr]' + JSON.stringify(action));
			}
			nextState[key] = nextStateForKey;
			hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
		}
		// 支持不完整的combineProcessor， 在有值变化的时候，将新的state合并到既有的state中去
		return hasChanged ? assign({}, state, nextState) : state;
	};
}

export default createStore;
export { combineProcessor };
